import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { Mail, Ticket } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TicketsPanel = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const statusFilter = filter !== 'all' ? `?status=${filter}` : '';
        const response = await fetch(`${API_URL}/api/contact/tickets/list${statusFilter}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setTickets(data);
        }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
    };
    fetchTickets();
  }, [filter]);

  const refreshTickets = async () => {
    const statusFilter = filter !== 'all' ? `?status=${filter}` : '';
    const response = await fetch(`${API_URL}/api/contact/tickets/list${statusFilter}`, {
      credentials: 'include'
    });
    if (response.ok) {
      setTickets(await response.json());
    }
  };

  const updateStatus = async (ticketId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/contact/tickets/${ticketId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success(`Ticket marked as ${newStatus}`);
        refreshTickets();
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const priorityColors = {
    high: 'bg-red-500/20 text-red-400 border-red-500/50',
    normal: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    low: 'bg-slate-500/20 text-slate-400 border-slate-500/50'
  };

  const statusColors = {
    open: 'bg-yellow-500/20 text-yellow-400',
    in_progress: 'bg-blue-500/20 text-blue-400',
    resolved: 'bg-green-500/20 text-green-400',
    closed: 'bg-slate-500/20 text-slate-400'
  };

  const categoryLabels = {
    sponsor: 'Sponsor',
    partner: 'Partner',
    advertiser: 'Advertiser',
    premium: 'Premium',
    ngo: 'NGO',
    ombudsman: 'Ombudsman',
    general: 'General'
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2">
            <Ticket className="w-5 h-5 text-purple-400" />
            Contact Inquiries & Tickets
          </CardTitle>
          <div className="flex gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-slate-700 text-white border-slate-600 rounded-md px-3 py-1 text-sm"
            >
              <option value="all">All Tickets</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="w-12 h-12 mx-auto text-slate-600 mb-3" />
            <p className="text-slate-400">No tickets found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div 
                key={ticket.id} 
                className="bg-slate-700/50 rounded-lg p-4 border border-slate-600 hover:border-slate-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={priorityColors[ticket.priority] || priorityColors.normal}>
                        {ticket.priority}
                      </Badge>
                      <Badge className={statusColors[ticket.status] || statusColors.open}>
                        {ticket.status?.replace('_', ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-slate-300 border-slate-500">
                        {categoryLabels[ticket.category] || ticket.category}
                      </Badge>
                    </div>
                    <h4 className="font-medium text-white truncate">{ticket.title}</h4>
                    <p className="text-sm text-slate-400 line-clamp-2 mt-1">{ticket.description}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    {ticket.status === 'open' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-blue-400 border-blue-500/50 hover:bg-blue-500/20"
                        onClick={() => updateStatus(ticket.id, 'in_progress')}
                      >
                        Start
                      </Button>
                    )}
                    {ticket.status === 'in_progress' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-green-400 border-green-500/50 hover:bg-green-500/20"
                        onClick={() => updateStatus(ticket.id, 'resolved')}
                      >
                        Resolve
                      </Button>
                    )}
                    {ticket.status !== 'closed' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-slate-400 border-slate-500/50 hover:bg-slate-500/20"
                        onClick={() => updateStatus(ticket.id, 'closed')}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Club status configurations

export default TicketsPanel;
